import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import { REHYDRATE } from "redux-persist/es/constants"

import { errorSave } from "../../../redux/errors.slice"
import type { RehydrateAction, RootState } from "../../../store.ts"
import * as services from "../api/services.ts"
import type {
  AccessibleBranchesResponse,
  Branch,
  BranchDetail,
  BranchSetupStatus,
  CompanyDashboardResponse
} from "../types/types.ts"
import { ALL_BRANCHES, branchesSliceName } from "./branches.constants.ts"

export const fetchAccessibleBranches = createAsyncThunk<
  AccessibleBranchesResponse,
  void
>(`${branchesSliceName}/fetchAccessible`, async (_, { dispatch, rejectWithValue }) => {
  const response = await services.getAccessibleBranches()
  if (!response.ok) {
    dispatch(errorSave(response?.data?.details))
    return rejectWithValue(response.data || { message: "Failed to fetch branches" })
  }
  return response.data
})

export const fetchBranches = createAsyncThunk<
  { count: number; results: Branch[] },
  { include_archived?: boolean } | void
>(`${branchesSliceName}/fetchBranches`, async (params, { dispatch, rejectWithValue }) => {
  const response = await services.getBranches(
    params?.include_archived ? { include_archived: "true" } : undefined
  )
  if (!response.ok) {
    dispatch(errorSave(response?.data?.details))
    return rejectWithValue(response.data || { message: "Failed to fetch branches" })
  }
  return response.data
})

export const fetchBranchDetail = createAsyncThunk<BranchDetail, string>(
  `${branchesSliceName}/fetchBranchDetail`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.getBranchDetail(id)
    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { message: "Failed to fetch branch" })
    }
    return response.data
  }
)

export const createBranchThunk = createAsyncThunk<BranchDetail, Record<string, unknown>>(
  `${branchesSliceName}/createBranch`,
  async (data, { dispatch, rejectWithValue }) => {
    const response = await services.createBranch(data)
    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { message: "Failed to create branch" })
    }
    return response.data
  }
)

export const updateBranchThunk = createAsyncThunk<
  BranchDetail,
  { id: string; data: Record<string, unknown> }
>(`${branchesSliceName}/updateBranch`, async ({ id, data }, { dispatch, rejectWithValue }) => {
  const response = await services.updateBranch(id, data)
  if (!response.ok) {
    dispatch(errorSave(response?.data?.details))
    return rejectWithValue(response.data || { message: "Failed to update branch" })
  }
  return response.data
})

export const archiveBranchThunk = createAsyncThunk<BranchDetail, string>(
  `${branchesSliceName}/archiveBranch`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.archiveBranch(id)
    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { message: "Failed to archive branch" })
    }
    return response.data
  }
)

export const restoreBranchThunk = createAsyncThunk<BranchDetail, string>(
  `${branchesSliceName}/restoreBranch`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.restoreBranch(id)
    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { message: "Failed to restore branch" })
    }
    return response.data
  }
)

export const fetchSetupStatus = createAsyncThunk<BranchSetupStatus, void>(
  `${branchesSliceName}/fetchSetupStatus`,
  async (_, { dispatch, rejectWithValue }) => {
    const response = await services.getSetupStatus()
    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { message: "Failed to fetch setup status" })
    }
    return response.data
  }
)

export const updateSetupTargetThunk = createAsyncThunk<BranchSetupStatus, number>(
  `${branchesSliceName}/updateSetupTarget`,
  async (target, { dispatch, rejectWithValue }) => {
    const response = await services.updateSetupTarget(target)
    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { message: "Failed to update the planned branch count" })
    }
    return response.data
  }
)

export const fetchCompanyDashboard = createAsyncThunk<
  CompanyDashboardResponse,
  { all: true } | { teams: string[] }
>(`${branchesSliceName}/fetchCompanyDashboard`, async (scope, { dispatch, rejectWithValue }) => {
  const response = await services.getCompanyDashboard(scope)
  if (!response.ok) {
    dispatch(errorSave(response?.data?.details))
    return rejectWithValue(response.data || { message: "Failed to fetch company dashboard" })
  }
  return response.data
})

// The company-level pages that each carry their own independent branch
// filter while the global selector is "All branches" (spec: a change on
// one must never affect another).
export type CompanyPageKey = "dashboard" | "reports" | "invoices" | "calendar" | "teamManagement"
export const COMPANY_PAGE_KEYS: CompanyPageKey[] = [
  "dashboard",
  "reports",
  "invoices",
  "calendar",
  "teamManagement"
]

export type PageScope = { type: "all" } | { type: "subset"; teamIds: string[] }

const ALL_SCOPE: PageScope = { type: "all" }

interface BranchesState {
  isOwner: boolean
  gcc: AccessibleBranchesResponse["gcc"]
  ownDefaultBranchId: string | null
  accessibleBranches: Branch[]
  managedBranches: Branch[]
  initialized: boolean
  // The ONE global, single-select header value: `ALL_BRANCHES` (the
  // minimal company nav) or one specific branch id (immediately the full
  // existing app, scoped to it — no separate "workspace mode" flag, the
  // value itself is the mode).
  globalBranch: string
  // Each of the five company pages' own independent multi-select filter,
  // live only while `globalBranch === ALL_BRANCHES`. Never read/written by
  // anything that isn't that exact page.
  pageFilters: Record<CompanyPageKey, PageScope>
  companyDashboard: CompanyDashboardResponse | null
  fetchingCompanyDashboard: boolean
  fetching: boolean
  fetchingManaged: boolean
  detail: BranchDetail | null
  fetchingDetail: boolean
  setupStatus: BranchSetupStatus | null
  // `fetchSetupStatus` is now dispatched from several places at once (Home,
  // Settings → Branches, every wizard step) — an older in-flight request
  // resolving after a newer one must not clobber the fresher state.
  setupStatusRequestId: string | null
  // Session-only (never persisted — outside `persistedBranchesState`'s
  // whitelist in store.ts). False until `fetchAccessibleBranches` has
  // resolved at least once THIS session. `globalBranch`/`pageFilters` can
  // rehydrate from localStorage before any request fires — on a fresh
  // login in a browser a different account last used, that persisted
  // value can name a branch id that belongs to a DIFFERENT company. The
  // selectors below refuse to hand out a specific-branch scope while this
  // is false, so no request ever carries an unvalidated id.
  accessibleBranchesValidated: boolean
}

const initialState: BranchesState = {
  isOwner: false,
  gcc: null,
  ownDefaultBranchId: null,
  accessibleBranches: [],
  managedBranches: [],
  initialized: false,
  globalBranch: ALL_BRANCHES,
  pageFilters: {
    dashboard: ALL_SCOPE,
    reports: ALL_SCOPE,
    invoices: ALL_SCOPE,
    calendar: ALL_SCOPE,
    teamManagement: ALL_SCOPE
  },
  companyDashboard: null,
  fetchingCompanyDashboard: false,
  fetching: false,
  fetchingManaged: false,
  detail: null,
  fetchingDetail: false,
  setupStatus: null,
  setupStatusRequestId: null,
  accessibleBranchesValidated: false
}

const branchesSlice = createSlice({
  name: branchesSliceName,
  initialState,
  reducers: {
    // The one action the top-header selector performs. Picking a specific
    // branch immediately means "show the full app, scoped to it" — no
    // separate open/close step. Picking `ALL_BRANCHES` returns to the
    // minimal owner nav; each page's own filter (below) is untouched by
    // this, and is simply irrelevant while a specific branch is active.
    setGlobalBranch: (state, action: { payload: string }) => {
      state.globalBranch = action.payload
    },
    // A single company page's own multi-select filter. Never accepts an
    // empty subset — the caller (the dropdown's Apply handler) must
    // resolve "nothing ticked" to `{ type: "all" }` before this ever
    // fires, so an empty selection can never silently mean "all" by
    // omission here. Setting one page's filter never touches another's.
    setPageFilter: (
      state,
      action: { payload: { page: CompanyPageKey; scope: PageScope } }
    ) => {
      state.pageFilters[action.payload.page] = action.payload.scope
    },
    // Dispatched on logout so a second account signing in on the same
    // browser — without a hard reload — never inherits the previous
    // account's in-memory branch scope, dashboard snapshot, or setup
    // status. Persisted `globalBranch`/`pageFilters` are also cleared here
    // (next persist write overwrites the stored blob); until the new
    // account's own `fetchAccessibleBranches` resolves, the selectors
    // below additionally refuse to hand out a stale scope from
    // `REHYDRATE` alone.
    resetBranchesState: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(REHYDRATE, (state, action: RehydrateAction) => {
        const persisted = action?.payload?.[branchesSliceName]
        if (!persisted) return state
        // Layered over initialState: only the remembered global branch and
        // per-page filters come back, so a partial persisted payload
        // cannot hollow out the rest of the slice.
        return {
          ...initialState,
          initialized: Boolean(persisted.initialized),
          globalBranch: persisted.globalBranch ?? initialState.globalBranch,
          pageFilters: { ...initialState.pageFilters, ...(persisted.pageFilters ?? {}) }
        }
      })
      .addCase(fetchAccessibleBranches.pending, (state) => {
        state.fetching = true
      })
      .addCase(fetchAccessibleBranches.fulfilled, (state, action) => {
        state.fetching = false
        state.accessibleBranchesValidated = true
        state.isOwner = action.payload.is_owner
        state.gcc = action.payload.gcc
        state.ownDefaultBranchId = action.payload.own_default_branch_id
        state.accessibleBranches = action.payload.branches
        const branches = action.payload.branches
        const validIds = new Set(branches.map((b) => b.id))
        const fallbackBranchId =
          action.payload.own_default_branch_id ?? branches[0]?.id ?? ALL_BRANCHES

        if (!state.initialized) {
          state.initialized = true
          // A non-owner (or a single-branch account) never sees "All
          // branches" at all — go straight to their one/default branch.
          state.globalBranch =
            action.payload.is_owner && branches.length > 1 ? ALL_BRANCHES : fallbackBranchId
          return
        }

        // Re-validate whatever persisted/rehydrated on every real fetch —
        // ownership can be transferred, a branch can be archived, a grant
        // can be revoked between sessions.
        if (state.globalBranch === ALL_BRANCHES) {
          // Company HQ mode stops making sense the moment the owner is
          // down to one (or zero) active branches — e.g. archived back
          // from two — so navigation simplifies back to the full
          // single-operation experience automatically. This is a live
          // recompute on every fetch, not a one-time decision made at
          // first load: an owner who archives a branch mid-session must
          // see this happen without a reload. The reverse (restoring a
          // branch while actively working IN a specific branch) never
          // forces a jump back to Company HQ here — only the workspace
          // selector's own visibility (accessibleBranches.length > 1)
          // reacts to that, so an owner's current operational context is
          // never disrupted by a branch count change elsewhere.
          if (!action.payload.is_owner || branches.length <= 1) {
            state.globalBranch = fallbackBranchId
          }
        } else if (!validIds.has(state.globalBranch)) {
          state.globalBranch = fallbackBranchId
        }
        for (const page of COMPANY_PAGE_KEYS) {
          const scope = state.pageFilters[page]
          if (scope.type === "subset") {
            const stillValid = scope.teamIds.filter((id) => validIds.has(id))
            state.pageFilters[page] = stillValid.length
              ? { type: "subset", teamIds: stillValid }
              : ALL_SCOPE
          }
        }
      })
      .addCase(fetchAccessibleBranches.rejected, (state) => {
        state.fetching = false
      })
      .addCase(fetchBranches.pending, (state) => {
        state.fetchingManaged = true
      })
      .addCase(fetchBranches.fulfilled, (state, action) => {
        state.fetchingManaged = false
        state.managedBranches = action.payload.results
      })
      .addCase(fetchBranches.rejected, (state) => {
        state.fetchingManaged = false
      })
      .addCase(fetchBranchDetail.pending, (state) => {
        state.fetchingDetail = true
      })
      .addCase(fetchBranchDetail.fulfilled, (state, action) => {
        state.fetchingDetail = false
        state.detail = action.payload
      })
      .addCase(fetchBranchDetail.rejected, (state) => {
        state.fetchingDetail = false
      })
      .addCase(createBranchThunk.fulfilled, (state, action) => {
        state.managedBranches = [...state.managedBranches, action.payload]
      })
      .addCase(updateBranchThunk.fulfilled, (state, action) => {
        state.managedBranches = state.managedBranches.map((b) =>
          b.id === action.payload.id ? { ...b, ...action.payload } : b
        )
        if (state.detail?.id === action.payload.id) {
          state.detail = { ...state.detail, ...action.payload }
        }
      })
      .addCase(archiveBranchThunk.fulfilled, (state, action) => {
        state.managedBranches = state.managedBranches.map((b) =>
          b.id === action.payload.id ? { ...b, ...action.payload } : b
        )
      })
      .addCase(restoreBranchThunk.fulfilled, (state, action) => {
        state.managedBranches = state.managedBranches.map((b) =>
          b.id === action.payload.id ? { ...b, ...action.payload } : b
        )
      })
      .addCase(fetchSetupStatus.pending, (state, action) => {
        state.setupStatusRequestId = action.meta.requestId
      })
      .addCase(fetchSetupStatus.fulfilled, (state, action) => {
        if (state.setupStatusRequestId !== action.meta.requestId) return
        state.setupStatus = action.payload
      })
      .addCase(updateSetupTargetThunk.fulfilled, (state, action) => {
        state.setupStatus = action.payload
      })
      .addCase(fetchCompanyDashboard.pending, (state) => {
        state.fetchingCompanyDashboard = true
      })
      .addCase(fetchCompanyDashboard.fulfilled, (state, action) => {
        state.fetchingCompanyDashboard = false
        state.companyDashboard = action.payload
      })
      .addCase(fetchCompanyDashboard.rejected, (state) => {
        state.fetchingCompanyDashboard = false
      })
  }
})

export const { setGlobalBranch, setPageFilter, resetBranchesState } = branchesSlice.actions

export const selectBranchesState = (state: RootState) => state.branches
// Deliberately NOT guarded by `accessibleBranchesValidated` (unlike
// `selectPageFilter` below): `globalBranch` only ever selects a single
// branch's OWN workspace, never builds the cross-branch `teams=` request
// the reported leak actually sent — and gating it the same way turned out
// to flip nearly every other test fixture's default render into "Company
// HQ mode" (nothing in this codebase dispatches `fetchAccessibleBranches`
// in a unit test unless it is specifically testing branch scoping), a far
// bigger behavior change than this fix calls for. A stale rehydrated
// `globalBranch` naming a foreign branch id is still real exposure for a
// skipped-logout browser switch; that is handled by `resetBranchesState`
// on logout instead, which carries none of this selector's blast radius.
export const selectGlobalBranch = (state: RootState) => state.branches.globalBranch
// Company HQ mode trims the sidebar to the company-level modules, and only
// the branch selector leads back out of it — so it applies exactly when that
// selector does: an owner of a multi-branch company with more than one
// branch. Anyone else whose saved branch reads "All branches" — the initial
// state, a failed branch fetch, or an account whose team has no company yet
// (`gcc: null`, an unfinished backfill) — gets the ordinary single-workspace
// navigation, as before multi-branch, instead of a view with no way out.
export const selectIsCompanyMode = (state: RootState) =>
  selectGlobalBranch(state) === ALL_BRANCHES &&
  state.branches.isOwner &&
  Boolean(state.branches.gcc?.multi_branch_enabled) &&
  state.branches.accessibleBranches.length > 1
// The one active branch when the global selector names a specific branch
// — `null` while it's "All branches".
export const selectActiveBranchId = (state: RootState) => {
  const branch = selectGlobalBranch(state)
  return branch === ALL_BRANCHES ? null : branch
}
export const selectAccessibleBranches = (state: RootState) => state.branches.accessibleBranches
export const selectIsOwner = (state: RootState) => state.branches.isOwner
export const selectIsMultiBranch = (state: RootState) =>
  state.branches.isOwner && state.branches.accessibleBranches.length > 1
// The company-level fact alone, independent of who's asking — unlike
// `selectIsMultiBranch` above (which also requires the CALLER to be the
// owner, right for deciding what an owner's own UI offers them), this is
// the real backend flag (`Gcc.multi_branch_enabled`, one-way once a second
// branch is ever activated) `BranchSelector.tsx` already reads directly.
// Use this whenever the question is "is this company actually multi-branch"
// rather than "does the current user personally administer more than one
// branch" — a single-operation account's one Team is always its own
// `activeBranchId` (see `selectActiveBranchId`'s own comment), so that
// alone can never distinguish "a real branch workspace" from "just the
// company," for ANY caller, owner or not.
export const selectCompanyIsMultiBranch = (state: RootState) =>
  !!state.branches.gcc?.multi_branch_enabled
export const selectCompanyDashboard = (state: RootState) => state.branches.companyDashboard
export const selectFetchingCompanyDashboard = (state: RootState) =>
  state.branches.fetchingCompanyDashboard
// Guarded by `accessibleBranchesValidated`: a persisted specific-branch
// subset is withheld until this session has validated it against a real
// accessible-branches fetch, so no page can build the cross-branch
// `teams=` request (the one `resolve_team_scope` actually 403'd on) from
// a previous account's ids.
export const selectPageFilter = (page: CompanyPageKey) => (state: RootState) =>
  state.branches.accessibleBranchesValidated ? state.branches.pageFilters[page] : ALL_SCOPE
export const selectSetupStatus = (state: RootState) => state.branches.setupStatus

// The active branch's own row from accessibleBranches, for display (name,
// default flag) wherever the app shows "which branch am I in".
export const selectActiveBranch = (state: RootState) => {
  const id = selectActiveBranchId(state)
  return id ? state.branches.accessibleBranches.find((b) => b.id === id) ?? null : null
}

export default branchesSlice
